import React, { useCallback, useEffect, useRef } from 'react';
import { cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { RovoThinkingBar } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBar';

import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

import { useCancelledAnalytics } from '../analytics.ts';
import { useSuggestionsLoadingMessages } from '../useRotatingLoadingMessage.ts';

const styles = cssMap({
	container: {
		// The suggestions list supplies the horizontal space.250 inset.
		paddingBlock: token('space.150'),
	},
});

type SuggestionsInlineLoadingBarProps = {
	/**
	 * Name of the agent doing the review, used to name it in the first loading message.
	 * Falls back to the generic Rovo copy when absent.
	 */
	agentName?: string;
	api?: FullPageEditorPresetAPI;
	/**
	 * Epoch ms when the thinking session started (from shared plugin state).
	 * Passed through so this bar stays in sync with the editor's thinking bar.
	 */
	startedAt?: number | null;
	/** Shared progress-bar facade target (0-100) for the thinking session. */
	progressTarget?: number | null;
};

/**
 * Compact loading bar shown at the bottom of the suggestions list while Rovo
 * regenerates with existing suggestions still visible. Reuses the editor's
 * `RovoThinkingBar` (without its gradient-border wrapper) so it stays in sync
 * with the full-page loading bar; we only own the padding and scroll-into-view.
 */
export const SuggestionsInlineLoadingBar = ({
	agentName,
	api,
	startedAt,
	progressTarget,
}: SuggestionsInlineLoadingBarProps): React.JSX.Element => {
	const containerRef = useRef<HTMLDivElement>(null);

	const rotatingMessages = useSuggestionsLoadingMessages({ agentName });
	const fireCancelledAnalytics = useCancelledAnalytics();

	// Scroll into view so the bar is visible with a long list above it.
	useEffect(() => {
		containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
	}, []);

	const handleStop = useCallback(() => {
		fireCancelledAnalytics();
		api?.aiSuggestions?.actions?.setThinkingState('idle');
	}, [api, fireCancelledAnalytics]);

	return (
		<div
			ref={containerRef}
			css={styles.container}
			data-testid="suggestions-panel-inline-loading-bar"
		>
			<RovoThinkingBar
				onStop={handleStop}
				showProgressBar
				useRotatingTitle
				customRotatingMessages={rotatingMessages}
				startedAt={startedAt}
				progressTarget={progressTarget}
			/>
		</div>
	);
};
